import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import api from '../services/api';
import { Building2, MapPin, ArrowLeft, Send, CheckCircle, Clock } from 'lucide-react';
import './RequestAccess.css';

interface Network {
  id: string;
  name: string;
  code: string;
}

interface Site {
  id: string;
  name: string;
  code: string;
}

interface AccessRequest {
  id: string;
  status: string;
  created_at: string;
  networks?: { name: string };
  sites?: { name: string };
}

const RequestAccess: React.FC = () => {
  const navigate = useNavigate();
  const [networks, setNetworks] = useState<Network[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [myRequests, setMyRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [userEmail, setUserEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [selectedSite, setSelectedSite] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedNetwork) {
      loadSites(selectedNetwork);
    } else {
      setSites([]);
      setSelectedSite('');
    }
  }, [selectedNetwork]);

  const loadData = async () => {
    try {
      // Get current user email
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setUserEmail(user.email);
        // Default name from email
        setFullName(user.email.split('@')[0].replace(/[._]/g, ' '));
      }

      // Load networks
      const networksRes = await api.get('/api/networks/list');
      setNetworks(networksRes.data);

      // Load user's existing requests
      try {
        const requestsRes = await api.get('/api/access-requests/my');
        setMyRequests(requestsRes.data);
      } catch (err) {
        // User might not have any requests yet
        setMyRequests([]);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadSites = async (networkId: string) => {
    try {
      const res = await api.get(`/api/networks/${networkId}/sites`);
      setSites(res.data);
    } catch (err) {
      console.error('Error loading sites:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await api.post('/api/access-requests', {
        email: userEmail,
        full_name: fullName,
        network_id: selectedNetwork || null,
        site_id: selectedSite || null
      });

      setSuccess(true);
      // Reload requests
      const requestsRes = await api.get('/api/access-requests/my');
      setMyRequests(requestsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const hasPendingRequest = myRequests.some(r => r.status === 'pending');

  if (loading) {
    return (
      <div className="request-access-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="request-access-container">
      <div className="request-access-card">
        <div className="request-logo" onClick={() => navigate('/')}>
          <span className="logo-icon">&#9678;</span>
          SiteOperations.io
        </div>
        <div className="request-access-header">
          <h1>Request Access</h1>
          <p>Submit a request to access a site. An administrator will review your request.</p>
        </div>

        {success ? (
          <div className="success-message">
            <CheckCircle size={48} />
            <h2>Request Submitted!</h2>
            <p>Your access request has been submitted. You will receive an email once an administrator reviews your request.</p>
            <button onClick={handleSignOut} className="btn-primary">
              Return to Login
            </button>
          </div>
        ) : (
          <>
            {/* Existing Requests */}
            {myRequests.length > 0 && (
              <div className="existing-requests">
                <h3>Your Requests</h3>
                <div className="requests-list">
                  {myRequests.map(request => (
                    <div key={request.id} className={`request-item status-${request.status}`}>
                      <div className="request-info">
                        <span className="request-target">
                          {request.sites?.name || request.networks?.name || 'Unknown'}
                        </span>
                        <span className="request-date">
                          {new Date(request.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <span className={`status-badge ${request.status}`}>
                        {request.status === 'pending' && <Clock size={14} />}
                        {request.status === 'approved' && <CheckCircle size={14} />}
                        {request.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Request Form */}
            {!hasPendingRequest ? (
              <form onSubmit={handleSubmit} className="request-form">
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                  <label>Your Email</label>
                  <input
                    type="email"
                    value={userEmail}
                    disabled
                    className="input-disabled"
                  />
                </div>

                <div className="form-group">
                  <label>Your Full Name</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <Building2 size={16} />
                    Select Network
                  </label>
                  <select
                    value={selectedNetwork}
                    onChange={(e) => setSelectedNetwork(e.target.value)}
                    required
                  >
                    <option value="">Choose a network...</option>
                    {networks.map(network => (
                      <option key={network.id} value={network.id}>
                        {network.name}
                      </option>
                    ))}
                  </select>
                </div>

                {sites.length > 0 && (
                  <div className="form-group">
                    <label>
                      <MapPin size={16} />
                      Select Site (Optional)
                    </label>
                    <select
                      value={selectedSite}
                      onChange={(e) => setSelectedSite(e.target.value)}
                    >
                      <option value="">Any site in network</option>
                      {sites.map(site => (
                        <option key={site.id} value={site.id}>
                          {site.name}
                        </option>
                      ))}
                    </select>
                    <span className="form-hint">
                      Leave blank to request access to any site in the network
                    </span>
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={submitting || !selectedNetwork}
                  >
                    {submitting ? (
                      'Submitting...'
                    ) : (
                      <>
                        <Send size={16} />
                        Submit Request
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <div className="pending-notice">
                <Clock size={32} />
                <p>You have a pending access request. Please wait for an administrator to review it.</p>
              </div>
            )}
          </>
        )}

        <div className="request-access-footer">
          <button onClick={handleSignOut} className="btn-link">
            <ArrowLeft size={16} />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestAccess;
